import React, { useState, useEffect, useRef } from "react"
import { CollapsableFilter } from "./collapsable_filter"
import { FilterSearchInput } from "./filter_search_input"
import {
  FilterDropdown,
  FilterDropdownGroup,
  FilterDropdownOption,
} from "./filter_dropdown"
import { IncludeExcludeTag } from "./include_exclude_tag"

const t = (key: string) => window.i18n.t(key)

type LocationType =
  | "continent"
  | "business_region"
  | "country"
  | "state"
  | "city"

interface LocationOption {
  label: string
  value: string
  type: LocationType
}

interface LocationFilterProps {
  includedContinentValues: string[]
  excludedContinentValues: string[]
  includedBusinessRegionValues: string[]
  excludedBusinessRegionValues: string[]
  includedCountryValues: string[]
  excludedCountryValues: string[]
  includedStateValues: string[]
  excludedStateValues: string[]
  includedCityValues: string[]
  excludedCityValues: string[]
  onChangeIncludedContinent: (values: string[]) => void
  onChangeExcludedContinent: (values: string[]) => void
  onChangeIncludedBusinessRegion: (values: string[]) => void
  onChangeExcludedBusinessRegion: (values: string[]) => void
  onChangeIncludedCountry: (values: string[]) => void
  onChangeExcludedCountry: (values: string[]) => void
  onChangeIncludedState: (values: string[]) => void
  onChangeExcludedState: (values: string[]) => void
  onChangeIncludedCity: (values: string[]) => void
  onChangeExcludedCity: (values: string[]) => void
  // Lookup dictionaries for displaying country/state names
  countryNames?: Record<string, string>
  stateNames?: Record<string, string>
}

interface SelectedLocation {
  value: string
  label: string
  type: LocationType
  isIncluded: boolean
}

// Helper to check if any location values exist in props
function hasInitialLocationValues(props: LocationFilterProps): boolean {
  return (
    props.includedContinentValues.length > 0 ||
    props.excludedContinentValues.length > 0 ||
    props.includedBusinessRegionValues.length > 0 ||
    props.excludedBusinessRegionValues.length > 0 ||
    props.includedCountryValues.length > 0 ||
    props.excludedCountryValues.length > 0 ||
    props.includedStateValues.length > 0 ||
    props.excludedStateValues.length > 0 ||
    props.includedCityValues.length > 0 ||
    props.excludedCityValues.length > 0
  )
}

export function LocationFilter(props: LocationFilterProps) {
  const [searchTerm, setSearchTerm] = useState("")
  const [searchResults, setSearchResults] = useState<LocationOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  // Cache labels from search API for continent/business_region/city
  const [searchLabels, setSearchLabels] = useState<
    Record<string, { label: string; type: LocationType }>
  >({})

  // Use props directly - they're already string arrays
  const {
    includedContinentValues,
    excludedContinentValues,
    includedBusinessRegionValues,
    excludedBusinessRegionValues,
    includedCountryValues,
    excludedCountryValues,
    includedStateValues,
    excludedStateValues,
    includedCityValues,
    excludedCityValues,
    countryNames,
    stateNames,
  } = props

  // Helper to get label for a location value
  const getLocationLabel = (value: string, type: LocationType): string => {
    // Use lookup dictionaries for country/state
    if (type === "country" && countryNames?.[value]) {
      return countryNames[value]
    }
    if (type === "state" && stateNames?.[value]) {
      return stateNames[value]
    }
    // Fall back to cached labels from search API (for continent/business_region/city)
    const key = `${type}:${value}`
    return searchLabels[key]?.label || value
  }

  // Calculate all selected locations
  const selectedLocations: SelectedLocation[] = [
    ...includedContinentValues.map((v) => ({
      value: v,
      label: getLocationLabel(v, "continent"),
      type: "continent" as LocationType,
      isIncluded: true,
    })),
    ...excludedContinentValues.map((v) => ({
      value: v,
      label: getLocationLabel(v, "continent"),
      type: "continent" as LocationType,
      isIncluded: false,
    })),
    ...includedBusinessRegionValues.map((v) => ({
      value: v,
      label: getLocationLabel(v, "business_region"),
      type: "business_region" as LocationType,
      isIncluded: true,
    })),
    ...excludedBusinessRegionValues.map((v) => ({
      value: v,
      label: getLocationLabel(v, "business_region"),
      type: "business_region" as LocationType,
      isIncluded: false,
    })),
    ...includedCountryValues.map((v) => ({
      value: v,
      label: getLocationLabel(v, "country"),
      type: "country" as LocationType,
      isIncluded: true,
    })),
    ...excludedCountryValues.map((v) => ({
      value: v,
      label: getLocationLabel(v, "country"),
      type: "country" as LocationType,
      isIncluded: false,
    })),
    ...includedStateValues.map((v) => ({
      value: v,
      label: getLocationLabel(v, "state"),
      type: "state" as LocationType,
      isIncluded: true,
    })),
    ...excludedStateValues.map((v) => ({
      value: v,
      label: getLocationLabel(v, "state"),
      type: "state" as LocationType,
      isIncluded: false,
    })),
    ...includedCityValues.map((v) => ({
      value: v,
      label: getLocationLabel(v, "city"),
      type: "city" as LocationType,
      isIncluded: true,
    })),
    ...excludedCityValues.map((v) => ({
      value: v,
      label: getLocationLabel(v, "city"),
      type: "city" as LocationType,
      isIncluded: false,
    })),
  ].sort((a, b) => a.label.localeCompare(b.label))

  const hasValues = selectedLocations.length > 0

  // Search for locations
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (!searchTerm.trim()) {
      setSearchResults([])
      setShowResults(false)
      return
    }

    const controller = new AbortController()

    searchTimeoutRef.current = setTimeout(async () => {
      setIsLoading(true)
      try {
        const response = await fetch(
          `/locations?query=${encodeURIComponent(searchTerm)}`,
          {
            headers: { Accept: "application/json" },
            signal: controller.signal,
          },
        )
        if (response.ok) {
          const data = await response.json()
          setSearchResults(data)
          setShowResults(true)
        }
      } catch (error) {
        // Silently handle non-abort errors
        if ((error as Error).name !== "AbortError") {
          setSearchResults([])
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    }, 300)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
      controller.abort()
    }
  }, [searchTerm])

  const handleSelectLocation = (locationValue: string) => {
    const location = searchResults.find(
      (r) => `${r.type}-${r.value}` === locationValue,
    )
    if (!location) return

    // Check if already selected
    const isAlreadySelected = selectedLocations.some(
      (l) => l.value === location.value && l.type === location.type,
    )

    if (isAlreadySelected) {
      setSearchTerm("")
      setShowResults(false)
      return
    }

    // Store the label for this location (for continent/business_region/city)
    const key = `${location.type}:${location.value}`
    setSearchLabels((prev) => ({
      ...prev,
      [key]: { label: location.label, type: location.type },
    }))

    // Add to included values by default
    addLocation(location.value, location.label, location.type, true)
    setSearchTerm("")
    setShowResults(false)
  }

  const addLocation = (
    value: string,
    label: string,
    type: LocationType,
    isIncluded: boolean,
  ) => {
    switch (type) {
      case "continent":
        if (isIncluded) {
          props.onChangeIncludedContinent([...includedContinentValues, value])
        } else {
          props.onChangeExcludedContinent([...excludedContinentValues, value])
        }
        break
      case "business_region":
        if (isIncluded) {
          props.onChangeIncludedBusinessRegion([
            ...includedBusinessRegionValues,
            value,
          ])
        } else {
          props.onChangeExcludedBusinessRegion([
            ...excludedBusinessRegionValues,
            value,
          ])
        }
        break
      case "country":
        if (isIncluded) {
          props.onChangeIncludedCountry([...includedCountryValues, value])
        } else {
          props.onChangeExcludedCountry([...excludedCountryValues, value])
        }
        break
      case "state":
        if (isIncluded) {
          props.onChangeIncludedState([...includedStateValues, value])
        } else {
          props.onChangeExcludedState([...excludedStateValues, value])
        }
        break
      case "city":
        if (isIncluded) {
          props.onChangeIncludedCity([...includedCityValues, value])
        } else {
          props.onChangeExcludedCity([...excludedCityValues, value])
        }
        break
    }
  }

  const removeLocation = (value: string, type: LocationType) => {
    switch (type) {
      case "continent":
        props.onChangeIncludedContinent(
          includedContinentValues.filter((v) => v !== value),
        )
        props.onChangeExcludedContinent(
          excludedContinentValues.filter((v) => v !== value),
        )
        break
      case "business_region":
        props.onChangeIncludedBusinessRegion(
          includedBusinessRegionValues.filter((v) => v !== value),
        )
        props.onChangeExcludedBusinessRegion(
          excludedBusinessRegionValues.filter((v) => v !== value),
        )
        break
      case "country":
        props.onChangeIncludedCountry(
          includedCountryValues.filter((v) => v !== value),
        )
        props.onChangeExcludedCountry(
          excludedCountryValues.filter((v) => v !== value),
        )
        break
      case "state":
        props.onChangeIncludedState(
          includedStateValues.filter((v) => v !== value),
        )
        props.onChangeExcludedState(
          excludedStateValues.filter((v) => v !== value),
        )
        break
      case "city":
        props.onChangeIncludedCity(
          includedCityValues.filter((v) => v !== value),
        )
        props.onChangeExcludedCity(
          excludedCityValues.filter((v) => v !== value),
        )
        break
    }
  }

  const toggleIncludeExclude = (location: SelectedLocation) => {
    removeLocation(location.value, location.type)
    addLocation(
      location.value,
      location.label,
      location.type,
      !location.isIncluded,
    )
  }

  const handleClear = () => {
    props.onChangeIncludedContinent([])
    props.onChangeExcludedContinent([])
    props.onChangeIncludedBusinessRegion([])
    props.onChangeExcludedBusinessRegion([])
    props.onChangeIncludedCountry([])
    props.onChangeExcludedCountry([])
    props.onChangeIncludedState([])
    props.onChangeExcludedState([])
    props.onChangeIncludedCity([])
    props.onChangeExcludedCity([])
    setSearchTerm("")
  }

  const getTypeLabel = (type: LocationType) => {
    return t(`js.back.discover.filters.location.types.${type}`)
  }

  // Group results by type
  const groupedResults = searchResults.reduce(
    (acc, result) => {
      if (!acc[result.type]) {
        acc[result.type] = []
      }
      acc[result.type].push(result)
      return acc
    },
    {} as Record<LocationType, LocationOption[]>,
  )

  return (
    <CollapsableFilter
      label={t("js.back.discover.filters.location.title")}
      icon="map-location-dot"
      tooltip={t("js.back.discover.filters.location.tooltip")}
      count={selectedLocations.length}
      hasValues={hasValues}
      onClear={handleClear}
      defaultExpanded={hasInitialLocationValues(props)}
    >
      <div className="position-relative">
        <FilterSearchInput
          id="location-search"
          label={t("js.back.discover.filters.location.title")}
          placeholder={t("js.back.discover.filters.location.placeholder")}
          value={searchTerm}
          onChange={setSearchTerm}
          onBlur={() => setShowResults(false)}
        />
        {isLoading && (
          <div className="h-field__append">
            <span
              className="far fa-spinner-third fa-spin"
              aria-label={t("js.back.discover.filters.loading")}
            />
          </div>
        )}
      </div>

      <FilterDropdown show={showResults && searchResults.length > 0}>
        {Object.entries(groupedResults).map(([type, results]) => (
          <FilterDropdownGroup
            key={type}
            header={getTypeLabel(type as LocationType)}
          >
            {results.map((result) => (
              <FilterDropdownOption
                key={`${result.type}-${result.value}`}
                value={`${result.type}-${result.value}`}
                onSelect={handleSelectLocation}
              >
                {result.label}
              </FilterDropdownOption>
            ))}
          </FilterDropdownGroup>
        ))}
      </FilterDropdown>

      {hasValues && (
        <div className="mt-2">
          {selectedLocations.map((location) => (
            <IncludeExcludeTag
              key={`${location.type}-${location.value}`}
              label={location.label}
              isIncluded={location.isIncluded}
              onToggle={() => toggleIncludeExclude(location)}
              onRemove={() => removeLocation(location.value, location.type)}
            />
          ))}
        </div>
      )}
    </CollapsableFilter>
  )
}
